// Block Cards — ScrollTrigger-driven reveal.
// As each card scrolls into view:
//   1. the card fades/rises in,
//   2. its SVG paths "self-draw" (stroke-dash, no premium plugin needed),
//   3. its number counts up from 0 to the ACF value.

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// SVG geometry elements that expose getTotalLength().
const DRAWABLE = "path, line, polyline, polygon, circle, rect, ellipse";

function prepareSvg(card) {
    const shapes = Array.from(card.querySelectorAll(DRAWABLE));
    return shapes
        .map((shape) => {
            let length = 0;
            try {
                length = shape.getTotalLength();
            } catch (e) {
                return null;
            }
            if (!length) return null;

            // Draw-on with the dash trick; fade the fill in afterwards so the
            // effect reads on both stroke- and fill-based icons.
            gsap.set(shape, {
                strokeDasharray: length,
                strokeDashoffset: length,
                fillOpacity: 0,
            });
            return shape;
        })
        .filter(Boolean);
}

function animateCard(card) {
    const number = card.querySelector(".block-cards__number");
    const shapes = prepareSvg(card);

    const tl = gsap.timeline({
        defaults: { ease: "power2.out" },
    });

    // Animate TO the visible state from the hidden state set at init — using
    // .to() (not .from()) keeps GSAP the single source of truth for opacity.
    tl.to(card, { y: 0, opacity: 1, duration: 0.6 });

    if (shapes.length) {
        tl.to(
            shapes,
            { strokeDashoffset: 0, duration: 1, stagger: 0.08 },
            "-=0.2"
        );
        tl.to(shapes, { fillOpacity: 1, duration: 0.4 }, "-=0.3");
    }

    if (number) {
        const target = parseFloat(number.dataset.count) || 0;
        const counter = { val: 0 };
        tl.to(
            counter,
            {
                val: target,
                duration: 1.2,
                ease: "power1.out",
                snap: { val: 1 },
                onUpdate: () => {
                    number.textContent = Math.round(counter.val).toLocaleString("it-IT");
                },
            },
            0
        );
    }
}

function revealImmediately(card) {
    card.classList.add("is-visible");
    const number = card.querySelector(".block-cards__number");
    if (number) {
        const target = parseFloat(number.dataset.count) || 0;
        number.textContent = target.toLocaleString("it-IT");
    }
    card.querySelectorAll(DRAWABLE).forEach((shape) => {
        gsap.set(shape, { strokeDashoffset: 0, fillOpacity: 1 });
    });
}

function initBlockCards(block) {
    const cards = Array.from(block.querySelectorAll("[data-card]"));
    if (!cards.length) return;

    const reduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
    ).matches;

    cards.forEach((card) => {
        if (reduceMotion) {
            revealImmediately(card);
            return;
        }

        // Establish the hidden state immediately (inline, owned by GSAP) so the
        // card is never seen before its ScrollTrigger fires.
        gsap.set(card, { opacity: 0, y: 40 });

        ScrollTrigger.create({
            trigger: card,
            // Fire once the card is well inside the viewport (its bottom edge
            // has passed ~85% of the way up), not as soon as it peeks in.
            start: "20% 90%",
            once: true,
            onEnter: () => animateCard(card),
        });
    });
}

function initAll() {
    document.querySelectorAll("[data-block-cards]").forEach(initBlockCards);
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
} else {
    initAll();
}
